// Copyright (c) Microsoft. All rights reserved.

import {
    Button,
    Input,
    InputOnChangeData,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuOpenChangeData,
    MenuPopover,
    MenuTrigger,
    Spinner,
    Subtitle2Stronger,
    makeStyles,
    mergeClasses,
    shorthands,
    tokens
} from '@fluentui/react-components';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useChat, useFile } from '../../../libs/hooks';
import { AlertType } from '../../../libs/models/AlertType';
import { Bot } from '../../../libs/models/Bot';
import { useAppDispatch, useAppSelector } from '../../../redux/app/hooks';
import { RootState } from '../../../redux/app/store';
import { FeatureKeys, IConversationDisplayMode } from '../../../redux/features/app/AppState';
import { addAlert, setConversationDisplayMode } from '../../../redux/features/app/appSlice';
import { Conversations } from '../../../redux/features/conversations/ConversationsState';
import { Breakpoints } from '../../../styles';
import { FileUploader } from '../../FileUploader';
import { CommentMultiple20, Dismiss20, Filter20 } from '../../shared/BundledIcons';
import { isToday } from '../../utils/TextUtils';
import { ChatListSection } from './ChatListSection';
import { NewBotMenu } from './bot-menu/NewBotMenu';
import { SimplifiedNewBotMenu } from './bot-menu/SimplifiedNewBotMenu';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexShrink: 0,
        width: '320px',
        backgroundColor: tokens.colorNeutralBackground4,
        flexDirection: 'column',
        ...shorthands.overflow('hidden'),
        ...Breakpoints.small({
            width: '64px',
        }),
    },
    list: {
        overflowY: 'auto',
        overflowX: 'hidden',
        '&:hover': {
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: tokens.colorScrollbarOverlay,
                visibility: 'visible',
            },
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: tokens.colorSubtleBackground,
        },
        alignItems: 'stretch',
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginRight: tokens.spacingVerticalM,
        marginLeft: tokens.spacingHorizontalXL,
        alignItems: 'center',
        height: '60px',
        minHeight: '60px',
        ...Breakpoints.small({
            justifyContent: 'center',
        }),
    },
    title: {
        flexGrow: 1,
        ...Breakpoints.small({
            display: 'none',
        }),
        fontSize: tokens.fontSizeBase500,
    },
    input: {
        flexGrow: 1,
        ...shorthands.padding(tokens.spacingHorizontalNone),
        ...shorthands.border(tokens.borderRadiusNone),
        backgroundColor: tokens.colorSubtleBackground,
        fontSize: tokens.fontSizeBase500,
    },
    noConversationLabel: {
        marginTop: 0,
        paddingBottom: tokens.spacingVerticalXS,
        marginLeft: tokens.spacingHorizontalXL,
        marginRight: tokens.spacingHorizontalXL,
        fontWeight: tokens.fontWeightRegular,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        ...Breakpoints.small({
            display: 'none',
        }),
    },
    conversationModeMenu: {
        ...Breakpoints.small({
            display: 'flex',
            flexDirection: 'column',
            marginLeft: '30px',
            alignItems: 'center',
        }),
    },
    hideOnSmall: {
        ...Breakpoints.small({
            'display': 'none',
        }),
    },
    showOnSmall: {
        display: 'none',
        ...Breakpoints.small({
            display: 'flex'
        })
    }
});

interface ConversationsView {
    filteredConversations?: Conversations;
    latestConversations?: Conversations;
    olderConversations?: Conversations;
}

enum AppState {
    ReloadingConversations,
    Chat
}

export const ChatList: FC = () => {
    const classes = useClasses();
    const { features } = useAppSelector((state: RootState) => state.app);
    const { conversations, isLoadingMessages } = useAppSelector((state: RootState) => state.conversations);

    const [isFiltering, setIsFiltering] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [conversationsView, setConversationsView] = useState<ConversationsView>({
        latestConversations: conversations,
    });

    const [selectedConversationOption, setSelectedConversationOption] = useState('Active conversations');

    const chat = useChat();
    const fileHandler = useFile();
    const dispatch = useAppDispatch();
    const [appState, setAppState] = useState(AppState.Chat);

    const sortConversations = (conversations: Conversations): ConversationsView => {
        // sort conversations by last activity
        const sortedIds = Object.keys(conversations).sort((a, b) => {
            if (conversations[a].lastUpdatedTimestamp === undefined) {
                return 1;
            }
            if (conversations[b].lastUpdatedTimestamp === undefined) {
                return -1;
            }
            // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
            return new Date(conversations[b].lastUpdatedTimestamp!).getTime() - new Date(conversations[a].lastUpdatedTimestamp!).getTime();
            // return conversations[a].lastUpdatedTimestamp! - conversations[b].lastUpdatedTimestamp!;
        });

        // Add conversations to sortedConversations in the order of sortedIds.
        const latestConversations: Conversations = {};
        const olderConversations: Conversations = {};
        sortedIds.forEach((id) => {
            if (isToday(new Date(conversations[id].lastUpdatedTimestamp ?? 0))) {
                latestConversations[id] = conversations[id];
            } else {
                olderConversations[id] = conversations[id];
            }
        });
        return {
            latestConversations: latestConversations,
            olderConversations: olderConversations,
        };
    };

    const onChangeHiddenChatsOption = (_selectedConversationOption: MenuOpenChangeData) => {
        
        if (_selectedConversationOption.type !== 'menuItemClick') {
            return;
        }

        const _conversationOption = (_selectedConversationOption.event.target as HTMLElement).innerText;
        
        setSelectedConversationOption(_conversationOption);
        setAppState(AppState.ReloadingConversations);

        switch (_conversationOption) {
            case 'Active conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Active));
                break;
            case 'Hidden conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.Hidden));
                break;
            case 'All conversations':
                dispatch(setConversationDisplayMode(IConversationDisplayMode.All));
                break;
        }

        if ( _conversationOption === 'Active conversations' || _conversationOption === 'All conversations') {
            void Promise.all([
                chat.loadChats(_conversationOption === 'All conversations').then((succeeded) => {
                    if (succeeded) {
                        setAppState(AppState.Chat);
                    }
                }),
            ]);
        } else {
            void Promise.all([
                chat.loadHiddenChats()
                .then((succeeded) => {
                    if (succeeded) {
                        setAppState(AppState.Chat);
                    } else {
                        console.error('Failed to load hidden chats');
                        setAppState(AppState.Chat);
                    }
                })
                .catch((error) => {
                    console.error('Failed to load hidden chats', error);
                    setAppState(AppState.Chat);
                }),
            ]);
        }
    };

    useEffect(() => {
        // Ensure local component state is in line with app state.
        if (filterText !== '') {
            // Reapply search string to the updated conversations list.
            const filteredConversations: Conversations = {};
            for (const key in conversations) {
                if (conversations[key].title.toLowerCase().includes(filterText.toLowerCase())) {
                    filteredConversations[key] = conversations[key];
                }
            }
            setConversationsView({ filteredConversations: filteredConversations });
        } else {
            // If no search string, show full conversations list.
            setConversationsView(sortConversations(conversations));
        }
    }, [conversations, filterText]);

    const onFilterClick = () => {
        setIsFiltering(true);
    };

    const onFilterCancel = () => {
        setFilterText('');
        setIsFiltering(false);
    };

    const onSearch = (ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) => {
        ev.preventDefault();
        setFilterText(data.value);
    };

    const fileUploaderRef = useRef<HTMLInputElement>(null);
    
    const onUpload = useCallback(
        (file: File) => {
            console.log('asdf');
            fileHandler.loadFile<Bot>(file, chat.uploadBot).catch((error) =>
                dispatch(
                    addAlert({
                        message: `Failed to parse uploaded file. ${error instanceof Error ? error.message : ''}`,
                        type: AlertType.Error,
                    }),
                ),
            );
        },
        [fileHandler, chat, dispatch],
    );

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                {features[FeatureKeys.HiddenChats].enabled && (
                    <div className={classes.conversationModeMenu}>
                    <Menu
                        onOpenChange={(_e, data) => {
                            onChangeHiddenChatsOption(data);
                        }}
                    >
                        <MenuTrigger disableButtonEnhancement>
                            <MenuButton
                                appearance="outline"
                                icon={<CommentMultiple20 />}
                                style={{ border: '1px solid rgb(189, 189, 189)'}}
                                disabled={appState === AppState.ReloadingConversations || isLoadingMessages}
                            >
                                <span className={classes.hideOnSmall}>{selectedConversationOption}</span>
                            </MenuButton>
                        </MenuTrigger>
                        <MenuPopover>
                            <MenuList>
                                <MenuItem>Active conversations</MenuItem>
                                <MenuItem>Hidden conversations</MenuItem>
                                <MenuItem>All conversations</MenuItem>
                            </MenuList>
                        </MenuPopover>
                    </Menu>
                        {features[FeatureKeys.SimplifiedExperience].enabled && (
                            <div className={ classes.showOnSmall }>
                                <SimplifiedNewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                            </div>
                        )}
                    </div>
                )}

                {features[FeatureKeys.SimplifiedExperience].enabled ? (
                    <SimplifiedNewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                ) : (
                    <>
                        {!isFiltering && (
                            <>
                                <Subtitle2Stronger className={classes.title}>Conversations</Subtitle2Stronger>
                                <Button icon={<Filter20 />} appearance="transparent" onClick={onFilterClick} />
                                <NewBotMenu onFileUpload={() => fileUploaderRef.current?.click()} />
                                <FileUploader
                                    ref={fileUploaderRef}
                                    acceptedExtensions={['.json']}
                                    onSelectedFile={onUpload}
                                />
                            </>
                        )}
                        {isFiltering && (
                            <>
                                <Input
                                    placeholder="Filter by name"
                                    className={mergeClasses(classes.input, classes.title)}
                                    onChange={onSearch}
                                    autoFocus
                                />
                                <Button icon={<Dismiss20 />} appearance="transparent" onClick={onFilterCancel} />
                            </>
                        )}
                    </>
                )}
            </div>
            <div aria-label={'chat list'} className={classes.list}>
                {appState !== AppState.ReloadingConversations ? (
                    <>
                        {Object.keys(conversations).length > 0 ? (
                            <>
                                {isFiltering && filterText.length > 0 ? (
                                    <>
                                        {conversationsView.filteredConversations && (
                                            <ChatListSection conversations={conversationsView.filteredConversations} />
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {conversationsView.latestConversations && (
                                            <ChatListSection
                                                header="Today"
                                                conversations={conversationsView.latestConversations}
                                            />
                                        )}
                                        {conversationsView.olderConversations && (
                                            <ChatListSection
                                                header="Older"
                                                conversations={conversationsView.olderConversations}
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        ) : (
                            <p className={classes.noConversationLabel}>No conversations</p>
                        )}
                    </>
                ) : (
                    <>
                        <Spinner label="Loading conversations..." className={classes.hideOnSmall} labelPosition="above" style={{ marginTop: '50%' }} />
                        <Spinner className={classes.showOnSmall} labelPosition="above" style={{ marginTop: '50%' }}/>
                    </>
                )}
            </div>
        </div>
    );
};
